import React, { useState, useEffect } from "react";
import { TextField, makeStyles, createStyles, Theme, MenuItem } from "@material-ui/core";
import { EventType } from "pages/statuses/EventType";
import { Laboratory } from "pages/statuses/Laboratory";
import { BookingApi } from "api/BookingApi";
import { ConfigGetter } from "utilities/ConfigGetter";
import * as ConfigKey from "utilities/ConfigGetter";
import { UserApi } from "api/UserApi";
import { UserDetail } from "api/entities/UserDetail";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        form: {
            // margin: theme.spacing(2),
            marginBottom: theme.spacing(1),
        },
        labo: {
            "& .MuiTextField-root": {
                width: `50%`,
            },
        },
        labelLabo: {
            marginLeft: theme.spacing(2),
            verticalAlign: `bottom`,
        },
        email: {
            "& .MuiTextField-root": {
                width: `50%`,
            },
        },
        labelRoot: {
            fontSize: `small`,
        },
    })
);

interface Props {
    // 予約情報
    booking: EventType;
    // ローディング状態のセッター
    setLoading: any;
}

/**
 * 予約登録コンポーネント
 * 予約者情報を登録するコンポーネント
 * @param props
 */
export const SubscribeParts: React.FC<Props> = (props) => {
    const { booking, setLoading } = props;
    const classes = useStyles();
    const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
    const emailDomainList: string[] = ConfigGetter.getList(ConfigKey.EMAIL_DOMAIN_LIST);

    /**
     * 指定した研究室情報から、選択状態の研究室IDを取得する
     * 合致する研究室IDがない場合は、空文字を返却する
     */
    const getSubscriberLaboratoryId = (laboratories: Laboratory[]): string => {
        const laboratory = laboratories.find((item) => {
            return item.id === booking.subscriberLaboratoryId && item.name === booking.subscriberLaboratoryName;
        });
        return laboratory?.id || ``;
    };
    const [inputSubscriberLaboratoryId, setInputSubscriberLaboratoryId] = useState<string>(
        getSubscriberLaboratoryId(laboratories)
    );

    /**
     * 指定した研究室情報から、選択状態の研究室名を取得する
     * 合致する研究室名がない場合は、空文字を返却する
     */
    const getSubscriberLaboratoryName = (laboratories: Laboratory[]): string => {
        const laboratory = laboratories.find((item) => {
            return (
                item.id === booking.subscriberLaboratoryId &&
                item.name === booking.subscriberLaboratoryName
            );
        });
        return laboratory?.name || ``;
    };
    const [inputSubscriberLaboratoryName, setInputSubscriberLaboratoryName] = useState<string>(
        getSubscriberLaboratoryName(laboratories)
    );

    const [inputSubscriberName, setInputSubscriberName] = useState<string | undefined>(booking.subscriberName);

    /**
     * メールアドレスをローカル部とドメイン部に分割する
     * メールアドレスが未設定の場合、ドメイン部はデフォルトを返却する
     */
    const getSeparateMailAddress = (mailAddress: string | undefined): { local: string; domain: string } => {
        if (mailAddress === undefined) {
            return {
                local: ``,
                domain: emailDomainList[0],
            };
        }
        const domain = emailDomainList.find((item) => item.replace(`@`, ``) === mailAddress.split(`@`)[1]);
        return {
            local: mailAddress.split(`@`)[0] || ``,
            domain: domain || emailDomainList[0],
        };
    };
    /**
     * メールアドレスをローカル部とドメイン部を結合する
     * ローカル部が空の場合、空文字を返却する
     */
    const getSubscriberMailAddress = (local: string, domain: string): string => {
        if (local === ``) return ``;
        return `${local}${domain}`;
    };
    const mailAddress = getSeparateMailAddress(booking.subscriberMailAddress);
    const [inputLocalMailAddress, setInputLocalMailAddress] = useState<string>(mailAddress.local);
    const [inputDomainMailAddress, setInputDomainMailAddress] = useState<string>(mailAddress.domain);
    const [inputSubscriberMailAddressOption, setInputSubscriberMailAddressOption] = useState<string | undefined>(
        booking.subscriberMailAddressOption
    );

    const [inputSubscriberTel, setInputSubscriberTel] = useState<string | undefined>(booking.subscriberTel);
    const [inputSubscriberMemo, setInputSubscriberMemo] = useState<string | undefined>(booking.subscriberMemo);

    /**
     * 指定された値が空かどうか
     * undefined、null、空文字の場合trueを返却する
     */
    const isEmpty = (value: any) => {
        return value === undefined || value === null || value === ``;
    };

    useEffect(() => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 全ての研究室情報を取得
            const response: any = await BookingApi.getLaboratories();
            if (response?.status === 200) {
                const laboratories: Laboratory[] = Array.from(response?.data);
                setLaboratories(laboratories);
                const laboratoryId = getSubscriberLaboratoryId(laboratories);
                setInputSubscriberLaboratoryId(laboratoryId);

                // 初期表示時はユーザー情報を表示、編集時は編集中の情報を表示
                if (
                    isEmpty(booking.subscriberLaboratoryId)
                ) {
                    const getUserDetailResponse = await UserApi.getUserDetail();
                    if (getUserDetailResponse?.status === 200) {
                        const user: UserDetail = getUserDetailResponse?.data;
                        setInputSubscriberLaboratoryName(user.laboName);
                        booking.subscriberLaboratoryId = user.laboId;
                        booking.subscriberLaboratoryName = user.laboName;
                    }
                } else {
                    const laboratoryName = getSubscriberLaboratoryName(laboratories);
                    setInputSubscriberLaboratoryName(laboratoryName);
                }
            }
            // ローディング終了
            setLoading(false);
        };
        execute();
    }, []);

    return (
        <div className={classes.form}>
            <div className={classes.labo}>
                <TextField
                    margin="dense"
                    id="laboratory"
                    label="研究室名"
                    value={inputSubscriberLaboratoryName}
                    fullWidth
                    inputProps={{
                        readOnly: true,
                    }}
                />
                <span className={classes.labelLabo}>研究室</span>
            </div>
            <TextField
                margin="dense"
                id="name"
                label="氏名（必須）"
                type="text"
                value={inputSubscriberName}
                onChange={(e) => {
                    // タイトルが入力されたら、その値をStateに登録する。
                    booking.subscriberName = e.target.value;
                    setInputSubscriberName(e.target.value);
                }}
                fullWidth
            />
            <div className={classes.email}>
                <TextField
                    margin="dense"
                    id="email"
                    label="1.メールアドレス（必須）"
                    type="text"
                    value={inputLocalMailAddress}
                    onChange={(e) => {
                        booking.subscriberMailAddress = getSubscriberMailAddress(
                            e.target.value,
                            inputDomainMailAddress
                        );
                        setInputLocalMailAddress(e.target.value);
                    }}
                    InputLabelProps={{
                        classes: {
                            root: classes.labelRoot,
                        }
                    }}
                />
                <TextField
                    select
                    margin="dense"
                    id="email_domain"
                    label="ドメイン"
                    value={inputDomainMailAddress}
                    onChange={(e) => {
                        booking.subscriberMailAddress = getSubscriberMailAddress(inputLocalMailAddress, e.target.value);
                        setInputDomainMailAddress(e.target.value);
                    }}
                >
                    {emailDomainList.map((domain, index) => {
                        return (
                            <MenuItem key={`${domain}${index}`} value={domain}>
                                {domain}
                            </MenuItem>
                        );
                    })}
                </TextField>
            </div>
            <TextField
                margin="dense"
                id="email_opt"
                label="2.メールアドレス（任意）"
                type="email"
                value={inputSubscriberMailAddressOption}
                onChange={(e) => {
                    booking.subscriberMailAddressOption = e.target.value;
                    setInputSubscriberMailAddressOption(e.target.value);
                }}
                fullWidth
            />
            <TextField
                margin="dense"
                id="tel"
                label="電話番号（任意）"
                type="text"
                value={inputSubscriberTel}
                onChange={(e) => {
                    booking.subscriberTel = e.target.value;
                    setInputSubscriberTel(e.target.value);
                }}
                fullWidth
            />
            <TextField
                margin="dense"
                id="memo"
                label="備考"
                type="text"
                value={inputSubscriberMemo}
                onChange={(e) => {
                    booking.subscriberMemo = e.target.value;
                    setInputSubscriberMemo(e.target.value);
                }}
                fullWidth
            />
        </div>
    );
};
